export const DEFAULT_RE_ORDER_SEGMENT_SLUG = "GROCERY";

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasValue = (value) => value !== null && value !== undefined && value !== "";

export const parseReOrderJsonValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const getPlainObjectPayload = (value) => {
  const parsedValue = parseReOrderJsonValue(value);

  if (isPlainObject(parsedValue)) return parsedValue;
  if (Array.isArray(parsedValue)) {
    return parsedValue.map(parseReOrderJsonValue).find(isPlainObject) || null;
  }

  return null;
};

export const getReOrderFirstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    const parsedSource = parseReOrderJsonValue(source);

    if (!isPlainObject(parsedSource)) continue;

    const matchedKey = keys.find((key) => hasValue(parsedSource?.[key]));
    if (matchedKey) return parsedSource[matchedKey];
  }

  return fallback;
};

export const getReOrderResponseMessage = (value, fallback = "Unable to re-order.") => {
  const parsedValue = parseReOrderJsonValue(value);
  const responseData = parseReOrderJsonValue(parsedValue?.response?.data);
  const data = parseReOrderJsonValue(parsedValue?.data);

  const message = getReOrderFirstValue(
    [responseData, data, parsedValue],
    ["message", "msg", "error", "error_message", "message_text"],
    fallback
  );

  return String(message || fallback);
};

export const getReOrderSaveCartPayload = (response = {}) => {
  const parsedResponse = parseReOrderJsonValue(response);
  const parsedData = parseReOrderJsonValue(parsedResponse?.data);
  const possiblePayloads = [
    parsedResponse?.response,
    parsedResponse?.response_data,
    parsedResponse?.cart_payload,
    parsedResponse?.cart_data,
    parsedData?.response,
    parsedData?.response_data,
    parsedData?.cart_payload,
    parsedData?.cart_data,
    parsedData,
  ];

  return possiblePayloads.map(getPlainObjectPayload).find(Boolean) || null;
};

const getReOrderDetailSources = (order = {}) => {
  const parsedOrder = parseReOrderJsonValue(order);
  const data = parseReOrderJsonValue(parsedOrder?.data);
  const receipt = parseReOrderJsonValue(parsedOrder?.receipt);
  const details = parseReOrderJsonValue(parsedOrder?.order_details || parsedOrder?.orderDetails);
  const nestedPayloads = [
    parsedOrder?.payload,
    parsedOrder?.reorder_payload,
    parsedOrder?.reorderPayload,
    parsedOrder?.re_order_payload,
    parsedOrder?.order_payload,
    parsedOrder?.orderPayload,
    data?.payload,
    data?.reorder_payload,
    data?.reorderPayload,
  ].map(parseReOrderJsonValue);

  return [parsedOrder, data, receipt, details, ...nestedPayloads].filter(isPlainObject);
};

export const getReOrderSegmentSlug = (
  order = {},
  fallback = DEFAULT_RE_ORDER_SEGMENT_SLUG
) => {
  const segmentSlug = getReOrderFirstValue(
    getReOrderDetailSources(order),
    [
      "segment_slug",
      "segmentSlug",
      "service_slug",
      "serviceSlug",
      "business_segment_slug",
      "businessSegmentSlug",
      "category_slug",
      "categorySlug",
      "slug",
      "segment_title",
      "segmentTitle",
      "segment_name",
      "segmentName",
    ],
    fallback
  );

  return String(segmentSlug || fallback).trim().toUpperCase();
};

export const getReOrderSegmentId = (order = {}, fallback = "") =>
  getReOrderFirstValue(
    getReOrderDetailSources(order),
    ["segment_id", "segmentId", "segment"],
    fallback
  );

export const getReOrderBookingOrderId = (order = {}, fallback = "") =>
  getReOrderFirstValue(
    getReOrderDetailSources(order),
    [
      "id",
      "booking_order_id",
      "bookingOrderId",
      "merchant_order_id",
      "merchantOrderId",
      "order_id",
      "orderId",
      "order_no",
      "orderNo",
      "order_number",
      "orderNumber",
    ],
    fallback
  );

export const buildReOrderRequestPayload = (
  order = {},
  fallbackSegmentSlug = DEFAULT_RE_ORDER_SEGMENT_SLUG
) => {
  const segmentSlug = getReOrderSegmentSlug(order, fallbackSegmentSlug);
  const segmentId = getReOrderSegmentId(order);
  const bookingOrderId = getReOrderBookingOrderId(order);
  const payload = new URLSearchParams();

  payload.append("segment_slug", segmentSlug);
  if (segmentId) payload.append("segment_id", segmentId);
  if (bookingOrderId) payload.append("booking_order_id", bookingOrderId);

  return {
    payload,
    segmentSlug,
    segmentId,
    bookingOrderId,
  };
};

export const normalizeReOrderSaveCartPayload = ({
  payload = {},
  ordersDetail = {},
  location = {},
  segmentId = "",
}) => {
  const receipt = parseReOrderJsonValue(ordersDetail?.receipt);
  const source = isPlainObject(payload) ? payload : {};
  const orderSources = getReOrderDetailSources(ordersDetail);
  const sources = [source, ...orderSources, receipt, location];
  const orderSegmentId = getReOrderSegmentId(ordersDetail, segmentId);
  const cartId =
    getReOrderFirstValue([source], ["cart_id", "cartId", "id"]) ||
    getReOrderFirstValue(orderSources, ["cart_id", "cartId"]);

  return {
    ...source,
    business_segment_id: getReOrderFirstValue(sources, [
      "business_segment_id",
      "businessSegmentId",
      "business_segment",
      "businessSegment",
      "store_id",
      "storeId",
      "branch_id",
    ]),
    cart_id: cartId,
    empty_bottle_count: getReOrderFirstValue(
      sources,
      ["empty_bottle_count", "emptyBottleCount"],
      ""
    ),
    home_or_office: getReOrderFirstValue(sources, ["home_or_office", "homeOrOffice"], 0),
    latitude: getReOrderFirstValue(sources, ["latitude", "lat"], location.latitude),
    longitude: getReOrderFirstValue(sources, ["longitude", "lng", "long"], location.longitude),
    product_details: getReOrderFirstValue(sources, ["product_details", "product_detail"], ""),
    product_update: getReOrderFirstValue(sources, ["product_update", "productUpdate"], "NO"),
    product_variant_id: getReOrderFirstValue(
      sources,
      ["product_variant_id", "productVariantId", "variant_id", "variantId"],
      0
    ),
    portion: getReOrderFirstValue(
      sources,
      [
        "portion",
        "product_portion",
        "productPortion",
        "selected_portion",
        "selectedPortion",
        "portion_name",
        "portionName",
      ],
      ""
    ),
    quantity: getReOrderFirstValue(sources, ["quantity", "qty"], 0),
    reward_points: getReOrderFirstValue(sources, ["reward_points", "rewardPoints"], 0),
    segment_id:
      orderSegmentId || getReOrderFirstValue(sources, ["segment_id", "segmentId", "segment"], 4),
    single_product: getReOrderFirstValue(sources, ["single_product", "singleProduct"], "NO"),
    merchant_id: getReOrderFirstValue(sources, ["merchant_id", "merchantId"], 1),
    area: getReOrderFirstValue(sources, ["area", "area_id", "country_area_id"], ""),
    "for": getReOrderFirstValue(sources, ["for", "cart_for", "cartFor"], 2),
    distance: getReOrderFirstValue(sources, ["distance", "store_distance", "storeDistance"], ""),
    cart_amount: getReOrderFirstValue(
      sources,
      ["cart_amount", "cartAmount", "item_total", "sub_total", "subtotal", "total_amount"],
      ""
    ),
  };
};
